import { reactive, ref, toRefs, watch } from 'vue'
import { defineStore } from 'pinia'
import type { relfilter, appfilter, fwfilter, pkgfilter, modelfilter, pagination} from '../types/types'

export const useUserStore = defineStore('role', () => {
  const role = ref(4)
  const isLoggedIn = ref(false);
  // config 설정 기본값
  // {"tablesize":20,"pnbtncount":3}
  const itemPerPage = ref(20) // 페이지당 아이템 수
  const siblingCount = ref(3) // 페이지네이션에서 현재 페이지 양옆에 표시할 페이지 수
  const account = ref('')
  const sessionExpiry = ref<number | null>(null)
  let sessionTimeout: ReturnType<typeof setTimeout> | null = null;
  let sessionprolongation: ReturnType<typeof setTimeout> | null = null;
  const isProlongationDialogOpen = ref(false);
  const isSessionDialogOpen = ref(false);

  // 메뉴 리스트
  const menus = ref<string[]>([])

  // Release 필터링 상태
  const relfilter = ref<relfilter>({
    chipset_id: 0,
    model_id: 0,
    page: 1,
    size: itemPerPage.value,
  })
  const relcurrentPage = ref<number>(0);

  // APP 필터링 상태
  const appfilter = ref<appfilter>({
    chipset_id: 0,
    package_id: 0,
    page: 1,
    size: itemPerPage.value,
  });
  const appcurrentPage = ref<number>(0);

  // FW 필터링 상태
  const fwfilter = ref<fwfilter>({
    chipset_id: 0,
    model_id: 0,
    fwtype_id: 0,
    page: 1,
    size: itemPerPage.value,
  });
  const fwcurrentPage = ref<number>(0);

  // PKG 필터링 상태
  const pkgfilter = ref<pkgfilter>({
    chipset_id: 0,
    pkgtype_id: 0,
    page: 1,
    size: itemPerPage.value,
  });
  const pkgcurrentPage = ref<number>(0);

  // Model 필터링 상태
  const modelfilter = ref<modelfilter>({
    chipset_id: 0,
    page: 1,
    size: itemPerPage.value,
  });

  const pkgtypePage = ref<pagination>({
    page: 1,
    size: itemPerPage.value,
  });
  const priorityPage = ref<pagination>({
    page: 1,
    size: itemPerPage.value,
  });
  const fwtypePage = ref<pagination>({
    page: 1,
    size: itemPerPage.value,
  });
  const chipsetPage = ref<pagination>({
    page: 1,
    size: itemPerPage.value,
  });

  watch(itemPerPage, () => {
    console.log('itemPerPage changed:', itemPerPage.value);
    relfilter.value.size = itemPerPage.value;
    appfilter.value.size = itemPerPage.value;
    fwfilter.value.size = itemPerPage.value;
    pkgfilter.value.size = itemPerPage.value;
    modelfilter.value.size = itemPerPage.value;
    pkgtypePage.value.size = itemPerPage.value;
    priorityPage.value.size = itemPerPage.value;
    fwtypePage.value.size = itemPerPage.value;
    chipsetPage.value.size = itemPerPage.value;

    relfilter.value.page = 1;
    appfilter.value.page = 1;
    fwfilter.value.page = 1;
    pkgfilter.value.page = 1;
    modelfilter.value.page = 1;
    pkgtypePage.value.page = 1;
    priorityPage.value.page = 1;
    fwtypePage.value.page = 1;
    chipsetPage.value.page = 1;
  })

  const perms = reactive({
    chipsetAdd: false, chipsetEdit: false, chipsetDelete: false,
    modelAdd: false, modelEdit: false, modelDelete: false,
    fwtypeAdd: false, fwtypeEdit: false, fwtypeDelete: false,
    pkgtypeAdd: false, pkgtypeEdit: false, pkgtypeDelete: false,
    pkgAdd: false, pkgEdit: false, pkgDelete: false,
    priorityAdd: false, priorityEdit: false, priorityDelete: false,
    fwAdd: false, fwEdit: false, fwDelete: false,
    appAdd: false, appEdit: false, appDelete: false,
    releaseAdd: false, releaseEdit: false, releaseDelete: false,
    releasenoteEdit: false,
    isDialogOpen: true
  })

  const resetPermissions = () => {
    Object.keys(perms).forEach(key => {
      perms[key as keyof typeof perms] = false
    })
  }

  // 타이머 등록 함수
  const setupTimeout = (Timeout: number) => {
    // 실행 중인 타이머가 있다면 제거
    if (sessionTimeout) clearTimeout(sessionTimeout);
    sessionTimeout = setTimeout(() => {
      isProlongationDialogOpen.value = false;
      clearTimer();
       isSessionDialogOpen.value = true;
    }, Timeout);

    // 세션만료 전 알림창 기능
    if (sessionprolongation) clearTimeout(sessionprolongation);
    sessionprolongation = setTimeout(() => {
      isProlongationDialogOpen.value = true;
      console.log('세션 연장 알림: 세션 만료 10분 전');
    }, Timeout - 600000); // 10분 전 알림
  };

  // 타이머 초기화 함수
  const clearTimer = () => {
    sessionExpiry.value = 0;
    sessionStorage.removeItem('timerTarget');
    if (sessionTimeout) {
      clearTimeout(sessionTimeout);
      sessionTimeout = null;
    }
    if (sessionprolongation) {
      clearTimeout(sessionprolongation);
      sessionprolongation = null;
    }
  };

  // 페이지 새로고침 타이머 복구
  const savedExpiry = sessionStorage.getItem('timerTarget');
  if (savedExpiry) {
    const expiryTime = parseInt(savedExpiry, 10);
    const currentTime = Date.now();
    const remainingTime = expiryTime - currentTime;

    if (remainingTime > 0) {
      // 아직 시간이 남았다면 스토어 상태 복구 및 타이머 재개
      sessionExpiry.value = expiryTime;
      setupTimeout(remainingTime);
      console.log(`세션 남은 시간: ${Math.round(remainingTime / 1000)}초`);
    } else {
      // 새로고침 시점에 이미 시간이 지나버렸다면 초기화
      clearTimer();
    }
  }
  
  // 세션 시작 함수 (로그인 시 호출)
  const startSession = (hours: number = 2) => {
    // 현재 시간 + (hours * 60분 * 60초 * 1000밀리초)
    sessionExpiry.value = Date.now() + hours * 60 * 60 * 1000;
    sessionStorage.setItem('timerTarget', sessionExpiry.value.toString());
    setupTimeout(sessionExpiry.value - Date.now());
    console.log(`세션 시작: 만료 시간 ${new Date(sessionExpiry.value).toLocaleString()}`);
  }

  // 세션 유효성 검사 함수
  const checkSession = () => {
    if (isLoggedIn.value && sessionExpiry.value) {
      if (Date.now() > sessionExpiry.value) {
        // 현재 시간이 만료 시간을 지났다면 로그아웃 처리
        return false; // 만료됨
      }
    }
    return true; // 유효함
  }
  
  return {
    role,
    ...toRefs(perms),
    relfilter,
    relcurrentPage,
    appfilter,
    appcurrentPage,
    fwfilter,
    fwcurrentPage,
    pkgfilter,
    pkgcurrentPage,
    modelfilter,
    priorityPage,
    itemPerPage,
    pkgtypePage,
    fwtypePage,
    chipsetPage,
    siblingCount,
    isLoggedIn,
    account,
    menus,
    sessionExpiry,
    resetPermissions,
    startSession,
    checkSession,
    isProlongationDialogOpen,
    isSessionDialogOpen,
  }

}, {
  //persist: true 
  persist: {
  storage: sessionStorage,
  }
})